import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { faImages } from '@fortawesome/pro-light-svg-icons/faImages';
import { TypedMemo } from '../../../../common/types';
import { LoadingView } from '../../../../common/views/LoadingView';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { getFullAPIFileURL } from '../../../../common/getFullAPIFileURL';
import { SMLEmptyState } from '../../../components/SMLEmptyState/SMLEmptyState';
import { revisionCardLabel } from '../../releases/assets/cardLabel';
import { mediaFactsLine } from '../composerMediaText';
import { pickerMessages } from './pickerMessages';
import { pickerAspectLabel } from './pickerFiles';
import { PickerTile } from './PickerTile';
import { ComposerMediaPicker } from './useComposerMediaPicker';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the picker; each part uses its own slice of it */
import styles from './ComposerMediaPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PickerFileListProps {
  picker: ComposerMediaPicker;
  onOpenStudio: (catalogItemUuid: string | undefined) => void;
}

/** The files of the picked song: a tile per newest ready revision, or the loading, error and empty states. */
const PickerFileListComponent: React.FC<PickerFileListProps> = ({ picker, onOpenStudio }) => {
  const intl = useIntl();
  const { song, files, allFiles, filter, selectedKey, select, attachKey, isLoading, error, refetch } = picker;
  const openStudio = useCallback(() => onOpenStudio(song), [onOpenStudio, song]);
  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  if (isLoading) {
    return <LoadingView />;
  }
  if (error !== undefined) {
    return <ErrorView error={error} retry={retry} />;
  }
  if (allFiles.length === 0) {
    return (
      <div data-testid="picker-empty">
        <SMLEmptyState
          icon={faImages}
          title={intl.formatMessage(song === undefined ? pickerMessages.noSongTitle : pickerMessages.emptyTitle)}
          description={intl.formatMessage(song === undefined ? pickerMessages.noSongBody : pickerMessages.emptyBody)}
          action={{ label: intl.formatMessage(pickerMessages.openStudio), onClick: openStudio }}
        />
      </div>
    );
  }
  if (files.length === 0) {
    return (
      <p className={styles.quietNote}>
        {intl.formatMessage(filter === 'image' ? pickerMessages.noImages : pickerMessages.noVideos)}
      </p>
    );
  }

  return (
    <ul className={styles.tileList} data-testid="picker-files">
      {files.map((file) => (
        <PickerTile
          key={file.key}
          tileKey={file.key}
          name={revisionCardLabel(intl, file.card)}
          facts={mediaFactsLine(intl, {
            kind: file.mediaKind,
            aspect: pickerAspectLabel(file),
            durationSec: file.durationSec,
          })}
          kind={file.mediaKind}
          pictureUrl={file.pictureUrl === null ? null : getFullAPIFileURL(file.pictureUrl)}
          badge={intl.formatMessage(pickerMessages.revision, { revision: file.revision })}
          isSelected={selectedKey === file.key}
          onSelect={select}
          onAttach={attachKey}
        />
      ))}
    </ul>
  );
};

export const PickerFileList = TypedMemo(PickerFileListComponent);
